import { ref, computed, readonly } from 'vue'
import { t } from '../i18n/index.js'

const KEY = 'OPERA.weather.units'
const state = ref(read())

function read() {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'F' ? 'F' : 'C'
  } catch {
    return 'C'
  }
}

function write(v) {
  try { localStorage.setItem(KEY, v) } catch { /* private mode */ }
}

// keep tabs in sync (OPERA does the same so open pages stay consistent)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) state.value = e.newValue === 'F' ? 'F' : 'C'
  })
}

/** Temperature unit switcher (°C / °F) with persistence. */
export function useUnits() {
  const unit = computed(() => state.value)
  const isMetric = computed(() => state.value === 'C')

  function setUnit(v) {
    state.value = v === 'F' ? 'F' : 'C'
    write(state.value)
  }

  /** Convert an API temperature to the display unit. API always returns `units=C` values. */
  function toDisplay(celsius) {
    if (celsius == null || Number.isNaN(celsius)) return null
    return state.value === 'C' ? Math.round(celsius) : Math.round(celsius * 9 / 5 + 32)
  }

  /** Convert a wind speed to the display unit. API always returns km/h, so imperial (°F) shows mph. */
  function speedToDisplay(kmh) {
    if (kmh == null || Number.isNaN(kmh)) return null
    return state.value === 'C' ? Math.round(kmh) : Math.round(kmh * 0.621371)
  }

  /** Convert a distance (km from the API) to the display unit. Imperial (°F) shows miles. */
  function distanceToDisplay(km) {
    if (km == null || Number.isNaN(km)) return null
    return state.value === 'C' ? km : km * 0.621371
  }

  /** Unit symbol, e.g. `°C`. */
  const symbol = computed(() => (state.value === 'C' ? '°C' : '°F'))
  const speedUnit = computed(() => t(state.value === 'C' ? 'unit.kmh.name' : 'unit.mph.name'))
  const distanceUnit = computed(() => t(state.value === 'C' ? 'unit.km.name' : 'unit.mi.name'))

  return {
    unit: readonly(unit),
    isMetric,
    setUnit,
    toDisplay,
    speedToDisplay,
    distanceToDisplay,
    symbol,
    speedUnit,
    distanceUnit
  }
}
